import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Divider, Container, Grid, Paper,
  Alert, CircularProgress, GlobalStyles, Backdrop, Link,
} from '@mui/material';
import { Microsoft as MicrosoftIcon } from '@mui/icons-material';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { loginUnificado, loginGoogleUnificado } from '../componentes/http/actions/loginUnificadoAction';
import { externalLoginUser } from '../componentes/http/actions/authAction';
import { msalInstance } from '../componentes/security/msal-config';
import Image from '../assets/Alerta Normativa-logo2026-claro.png';
import ImageBlanco from '../assets/Alerta Normativa-logo2026-blanco.png';
import { useColorMode } from '../styles/ColorModeProvider';

// URL del formulario de suscripción (para Google no registrado)
const URL_FORMULARIO = 'https://alertanormativa.com/suscripcion-normativa/';

// Lee el rol del JWT para decidir a dónde redirigir tras login.
const rolDeToken = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]))["rol"] || null;
  } catch {
    return null;
  }
};

const LoginForm = () => {
  const navigate = useNavigate();
  const { mode } = useColorMode();
  const isDark = mode === 'dark';

  const [loginData, setLoginData] = useState({ Email: '', Password: '' });
  const [loginError, setLoginError] = useState('');
  const [noRegistrado, setNoRegistrado] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeLoginMethod, setActiveLoginMethod] = useState(null);
  const [isUILocked, setIsUILocked] = useState(false);

  useEffect(() => {
    let t;
    if (loginError) t = setTimeout(() => setLoginError(''), 6000);
    return () => t && clearTimeout(t);
  }, [loginError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };

  // Guarda el token y redirige según rol. CLAVE: guarda ANTES de navegar.
  const entrarConToken = (token) => {
    localStorage.setItem('token', token);
    const rol = rolDeToken(token);
    const destino = rol === 'Suscriptor' ? '/suscriptor/bandeja' : '/home';
    navigate(destino, { replace: true });
  };

  // ── Login email + password (cascada interno → suscriptor) ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || isUILocked) return;

    setLoginError('');
    setNoRegistrado(false);
    setIsLoading(true);
    setActiveLoginMethod('standard');

    try {
      const res = await loginUnificado(loginData.Email.trim(), loginData.Password);
      if (res.ok && res.token) {
        entrarConToken(res.token);
      } else {
        setLoginError(res.message || 'Correo o contraseña incorrectos.');
        setIsLoading(false);
        setActiveLoginMethod(null);
      }
    } catch (error) {
      console.error('Error en login:', error);
      setLoginError('No se pudo conectar con el servidor.');
      setIsLoading(false);
      setActiveLoginMethod(null);
    }
  };

  // ── Login Google (cascada interno → suscriptor) ──
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsUILocked(false);
    if (isLoading) return;
    if (!credentialResponse?.credential) {
      setLoginError('Error al iniciar sesión con Google. Inténtelo de nuevo.');
      return;
    }

    setIsLoading(true);
    setLoginError('');
    setNoRegistrado(false);
    setActiveLoginMethod('google');

    try {
      const res = await loginGoogleUnificado(credentialResponse.credential);
      if (res.ok && res.token) {
        entrarConToken(res.token);
      } else if (res.noRegistrado) {
        setNoRegistrado(true);
        setIsLoading(false);
        setActiveLoginMethod(null);
      } else {
        setLoginError(res.message || 'No se pudo iniciar sesión con Google.');
        setIsLoading(false);
        setActiveLoginMethod(null);
      }
    } catch (error) {
      console.error('Error en login Google:', error);
      setLoginError('No se pudo conectar con el servidor.');
      setIsLoading(false);
      setActiveLoginMethod(null);
    }
  };

  const handleGoogleError = () => {
    setIsUILocked(false);
    setLoginError('Error al iniciar sesión con Google. Inténtelo de nuevo.');
    setActiveLoginMethod(null);
    setIsLoading(false);
  };

  // ── Login Microsoft (solo internos; usa tu flujo existente) ──
  const handleMicrosoftLogin = async () => {
    if (isLoading || isUILocked) return;

    setIsUILocked(true);
    setIsLoading(true);
    setLoginError('');
    setActiveLoginMethod('microsoft');

    try {
      const loginRequest = { scopes: ['user.read', 'profile', 'email', 'openid'] };
      await msalInstance.initialize();
      const response = await msalInstance.loginPopup(loginRequest);

      if (response && response.idToken) {
        // Reutiliza tu action existente para Microsoft (interno).
        // externalLoginUser guarda el token vía su callback setToken.
        let capturado = null;
        const result = await externalLoginUser(
          'Microsoft',
          response.idToken,
          setLoginError,
          (tok) => { capturado = tok; localStorage.setItem('token', tok); }
        );
        if (result && capturado) {
          const rol = rolDeToken(capturado);
          navigate(rol === 'Suscriptor' ? '/suscriptor/bandeja' : '/home', { replace: true });
        } else {
          setIsLoading(false);
          setActiveLoginMethod(null);
        }
      } else {
        setLoginError('No se pudo obtener el token de autenticación');
        setIsLoading(false);
        setActiveLoginMethod(null);
      }
    } catch (error) {
      console.error('Error Microsoft:', error);
      setIsLoading(false);
      setActiveLoginMethod(null);
    } finally {
      setIsUILocked(false);
    }
  };

  const globalStyles = {
    '@keyframes fadeIn': {
      from: { opacity: 0, transform: 'translateY(-10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  };

  return (
    <>
      <GlobalStyles styles={globalStyles} />
      <Backdrop
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1, backgroundColor: 'rgba(0,0,0,0.2)' }}
        open={isUILocked}
        onClick={() => setIsUILocked(false)}
      />

      <Container maxWidth="xs" sx={{ minHeight: '100vh' }}>
        <Grid container direction="row" alignItems="center" justifyContent="center" sx={{ mt: 6 }}>
          <Paper elevation={1} sx={{ width: '100%' }}>
            <Grid container sx={{ p: 3 }}>
              <Grid item xs={12} container justifyContent="center" sx={{ mb: 2 }}>
                <Box component="img" src={isDark ? ImageBlanco : Image} alt="Alerta Normativa" sx={{ height: 100, mb: 2 }} />
              </Grid>

              {loginError && (
                <Alert severity="error" sx={{ width: '100%', mb: 2, animation: 'fadeIn 0.3s ease-in-out', borderRadius: '8px' }} onClose={() => setLoginError('')}>
                  {loginError}
                </Alert>
              )}

              {noRegistrado && (
                <Alert severity="warning" sx={{ width: '100%', mb: 2, animation: 'fadeIn 0.3s ease-in-out', borderRadius: '8px' }}>
                  No estás registrado con este correo.
                  <Box sx={{ mt: 1 }}>
                    <Link href={URL_FORMULARIO} sx={{ fontWeight: 600 }} target="_self" rel="noopener">
                      Regístrate aquí →
                    </Link>
                  </Box>
                </Alert>
              )}

              <Grid item xs={12}>
                <Typography variant="h5" align="center" sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
                  Inicio de Sesión
                </Typography>
              </Grid>

              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                <TextField
                  margin="normal" required fullWidth id="email" label="Correo electrónico"
                  name="Email" autoComplete="email" autoFocus
                  value={loginData.Email} onChange={handleChange}
                  disabled={isLoading || isUILocked}
                />
                <TextField
                  margin="normal" required fullWidth name="Password" label="Contraseña"
                  type="password" id="password" autoComplete="current-password"
                  value={loginData.Password} onChange={handleChange}
                  disabled={isLoading || isUILocked}
                />
                <Button
                  type="submit" fullWidth variant="contained"
                  sx={{ mt: 3, mb: 2, fontWeight: 600, padding: '12px', borderRadius: '8px', fontSize: '1rem' }}
                  disabled={isLoading || isUILocked}
                >
                  {activeLoginMethod === 'standard' && isLoading ? <CircularProgress size={24} color="inherit" /> : 'Iniciar Sesión'}
                </Button>
              </Box>

              <Divider sx={{ width: '100%', my: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>O continuar con</Typography>
              </Divider>

              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  fullWidth variant="outlined" color="inherit"
                  onClick={handleMicrosoftLogin} disabled={isLoading || isUILocked}
                  sx={{
                    display: 'flex', alignItems: 'center', position: 'relative', justifyContent: 'center',
                    height: '40px', paddingLeft: '36px', paddingRight: '16px', borderRadius: '6px',
                    fontWeight: 600, borderColor: 'divider', color: 'text.primary',
                    '&:hover': { borderColor: 'text.secondary', backgroundColor: 'action.hover' },
                  }}
                >
                  <Box sx={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {activeLoginMethod === 'microsoft' && isLoading ? <CircularProgress size={20} color="primary" /> : <MicrosoftIcon sx={{ width: 24, height: 24, color: 'text.primary' }} />}
                  </Box>
                  Iniciar sesión con Microsoft
                </Button>

                <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    size="large" width={355} logo_alignment="left"
                    theme={isDark ? 'filled_black' : 'outline'}
                  />
                </GoogleOAuthProvider>
              </Box>

              {isLoading && !activeLoginMethod && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, width: '100%' }}>
                  <CircularProgress size={28} color="primary" />
                </Box>
              )}

              {/* Footer con enlace a suscripción 
              <Grid item xs={12}>
                <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 3, fontSize: '0.875rem' }}>
                  ¿Aún no tienes cuenta?{' '}
                  <Link href={URL_FORMULARIO} sx={{ fontWeight: 600 }} target="_self" rel="noopener">
                    Suscríbete aquí
                  </Link>
                </Typography>
              </Grid>*/}
            </Grid>
          </Paper>
        </Grid>
      </Container>
    </>
  );
};

export default LoginForm;